import React from 'react';
import clsx from 'clsx';
import {makeStyles} from 'tss-react/mui';
import {TableCell, TableHead, TableRow} from '@mui/material';
import {PtrnTablaBaseHeadProps, PtrnTablaBaseHeadRowProps} from './PtrnTablaBaseTypes';
import {PtrnAlineacion} from '../../patronTypes';

const useStyles = makeStyles()((theme) => ({
   tHeadCell: {
      color: theme.palette.secondary.contrastText,
      backgroundColor: theme.palette.secondary.main,
      border: 'none',
      fontWeight: 'bold',
      paddingTop: 4,
      paddingBottom: 4,
      paddingLeft: 8,
      paddingRight: 8
   },
   tHdCllTopLeft: {
      borderTopLeftRadius: 8
   },
   tHdCllBotLeft: {
      borderBottomLeftRadius: 8
   },
   tHdCllTopRight: {
      borderTopRightRadius: 8
   },
   tHdCllBotRight: {
      borderBottomRightRadius: 8
   }
}));

function PtrnTablaBaseHeadRow({
   camposConfigPrimitivas,
   configCols,
   accionCeldaStyle,
   existenAccionesFila,
   headerIndex,
   nroCabeceras,
   alturaFilaCabecera,
   existeData
}: PtrnTablaBaseHeadRowProps & {headerIndex?: number}) {
   const {classes} = useStyles();

   //#region helper style
   headerIndex = headerIndex ?? 0;
   const nroCabecerasBase0 = (nroCabeceras ?? 1) - 1;
   const isFirstRow = headerIndex === 0;
   //#endregion

   //#region ret main
   const idPrefix = `tbase-thead-row-${headerIndex}`;
   //para sticky multi header configura posicion absoluta de la fila dependiendo de su indice
   const multiStickyHeaderStyle = {
      top: (alturaFilaCabecera ?? 32) * headerIndex
   };
   //para poder determinar correctamente los estilos de las columnas visibles
   const cantidadColsvisibles = camposConfigPrimitivas.reduce((prev, col) => {
      const {colSpan = 1, rowSpan = 1} = col.enTabla?.headers
         ? col.enTabla.headers[headerIndex]
         : {};
      if (colSpan === 0 || rowSpan === 0) {
         return prev;
      }
      const currColConf = configCols[col.name];
      if (currColConf.oculto) {
         return prev;
      }

      return prev + 1;
   }, 0);

   return (
      <TableRow id={idPrefix}>
         {camposConfigPrimitivas.map((col, colInx, arr) => {
            //verificar q la columna tiene configuracion valida
            const currColConf = configCols[col.name];
            if (!currColConf) {
               return null;
            }
            //verificar o crear un configura de headers
            const {
               colSpan = 1,
               rowSpan = 1,
               width = undefined,
               label = undefined
            } = col.enTabla?.headers ? col.enTabla.headers[headerIndex] : {};
            if (colSpan === 0 || rowSpan === 0) {
               return null;
            }

            const idHeadCell = `${idPrefix}-cell-${col.name}`;
            if (currColConf.oculto) {
               return null;
            }
            //#region estilos
            const isLastRow = headerIndex + rowSpan - 1 === nroCabecerasBase0;
            const isFirstCol = colInx === 0;
            const isLastCol =
               !existenAccionesFila &&
               (colInx === arr.length - 1 || colSpan + colInx > cantidadColsvisibles);
            const currClassName = clsx(classes.tHeadCell, {
               [classes.tHdCllTopLeft]: isFirstRow && isFirstCol,
               [classes.tHdCllBotLeft]: isLastRow && isFirstCol,
               [classes.tHdCllTopRight]: isFirstRow && isLastCol,
               [classes.tHdCllBotRight]: isLastRow && isLastCol
            });

            //para sticky multi header
            let currWidth = '100%';
            if (existeData) {
               currWidth = width || currColConf?.style?.width;
            }

            const finalStyle = {
               ...currColConf?.style,
               ...multiStickyHeaderStyle,
               width: currWidth
            };
            //#endregion
            //#region ret header cell
            return (
               <TableCell
                  id={idHeadCell}
                  colSpan={colSpan}
                  rowSpan={rowSpan}
                  key={idHeadCell}
                  align={colSpan > 1 ? PtrnAlineacion.center : currColConf?.alinear}
                  style={finalStyle}
                  className={currClassName}
                  title={col.label}
               >
                  {label != undefined ? label : col.label}
               </TableCell>
            );
            //#endregion
         })}
         {existenAccionesFila && (
            //#region ret header acciones
            <TableCell
               id={`${idPrefix}-thead-row-cell-acciones`}
               style={{...accionCeldaStyle, ...multiStickyHeaderStyle}}
               className={clsx(classes.tHeadCell, {
                  [classes.tHdCllTopRight]: isFirstRow,
                  [classes.tHdCllBotRight]: headerIndex === nroCabecerasBase0
               })}
               align="right"
            />
            //#endregion
         )}
      </TableRow>
   );
   //#endregion
}

function PtrnTablaBaseHead({
   camposConfigPrimitivas,
   configCols,
   nroCabeceras,
   alturaFilaCabecera,
   accionCeldaStyle,
   existenAccionesFila,
   existeData,
   ignorarColGroup
}: PtrnTablaBaseHeadProps & PtrnTablaBaseHeadRowProps) {
   //#region ret main
   return (
      <>
         {!ignorarColGroup && (
            <colgroup>
               {camposConfigPrimitivas.map((col, colInx, arr) => {
                  const currColConf = configCols[col.name];
                  if (!currColConf) {
                     return null;
                  }

                  if (!col.enTabla?.width) {
                     return null;
                  }

                  return <col key={col.name} style={{width: col.enTabla.width}} />;
               })}
               {existenAccionesFila && <col style={{width: 'auto'}} />}
            </colgroup>
         )}

         <TableHead id="tbase-thead">
            {[...Array(nroCabeceras)].map((_, inxHeader) => {
               return (
                  <PtrnTablaBaseHeadRow
                     key={inxHeader}
                     camposConfigPrimitivas={camposConfigPrimitivas}
                     configCols={configCols}
                     headerIndex={inxHeader}
                     nroCabeceras={nroCabeceras}
                     alturaFilaCabecera={alturaFilaCabecera}
                     accionCeldaStyle={accionCeldaStyle}
                     existenAccionesFila={existenAccionesFila}
                     existeData={existeData}
                  />
               );
            })}
         </TableHead>
      </>
   );
   //#endregion
}

export default PtrnTablaBaseHead;
